import React from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '../lib/utils';

/**
 * A small Markdown renderer for assistant replies.
 *
 * It builds React elements rather than an HTML string on purpose: model output
 * is untrusted (it can quote a document that itself contains markup), and
 * dangerouslySetInnerHTML on that path is an XSS vector. Rendering to elements
 * means any HTML in the reply is displayed as the text it is.
 *
 * Scope is deliberately the subset a chat reply actually uses — fenced code,
 * headings, lists, bold/italic, inline code, links, rules. No tables, images or
 * block quotes; add them here if replies start needing them.
 */

type Inline = React.ReactNode;

/** Split a run of text on the first matching delimiter, recursing on the rest. */
function renderInline(text: string, keyPrefix: string): Inline[] {
  const out: Inline[] = [];
  let rest = text;
  let key = 0;

  // Order matters: code first, so `**` inside a code span is left alone.
  const patterns: Array<{
    re: RegExp;
    render: (m: RegExpMatchArray, k: string) => Inline;
  }> = [
    {
      re: /`([^`]+)`/,
      render: (m, k) => (
        <code
          key={k}
          className="rounded border border-border bg-muted px-1 py-px font-mono text-[0.855em]"
        >
          {m[1]}
        </code>
      ),
    },
    {
      re: /\[([^\]]+)\]\(([^)\s]+)\)/,
      render: (m, k) => (
        <a
          key={k}
          href={m[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--assistant-accent)] underline underline-offset-2"
        >
          {m[1]}
        </a>
      ),
    },
    {
      re: /\*\*([^*]+)\*\*/,
      render: (m, k) => (
        <strong key={k} className="font-semibold">
          {renderInline(m[1], k)}
        </strong>
      ),
    },
    {
      re: /(?<![*\w])\*([^*\n]+)\*(?!\w)/,
      render: (m, k) => (
        <em key={k} className="italic">
          {renderInline(m[1], k)}
        </em>
      ),
    },
  ];

  outer: while (rest.length > 0) {
    let best: { index: number; match: RegExpMatchArray; pattern: (typeof patterns)[number] } | null =
      null;

    for (const pattern of patterns) {
      const match = rest.match(pattern.re);
      if (match && match.index !== undefined) {
        if (best === null || match.index < best.index) {
          best = { index: match.index, match, pattern };
        }
      }
    }

    if (best === null) {
      out.push(rest);
      break outer;
    }

    if (best.index > 0) out.push(rest.slice(0, best.index));
    out.push(best.pattern.render(best.match, `${keyPrefix}-i${key++}`));
    rest = rest.slice(best.index + best.match[0].length);
  }

  return out;
}

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  const [copied, setCopied] = React.useState(false);

  const copy = React.useCallback(() => {
    navigator.clipboard.writeText(code).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1400);
      },
      () => {
        /* clipboard blocked — the text is selectable, so leave it */
      },
    );
  }, [code]);

  return (
    <div className="mb-2.5 overflow-hidden rounded-md border border-border bg-muted">
      <div className="flex items-center border-b border-border py-1 pl-2.5 pr-1.5">
        <span className="font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground">
          {lang}
        </span>
        <button
          type="button"
          onClick={copy}
          className="ml-auto flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] text-muted-foreground transition-colors hover:bg-background hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="overflow-x-auto p-2.5 font-mono text-[11.5px] leading-relaxed">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export function Markdown({ source, className }: { source: string; className?: string }) {
  const blocks: React.ReactNode[] = [];
  const lines = source.split('\n');
  let i = 0;
  let key = 0;

  while (i < lines.length) {
    const line = lines[i];

    const fence = line.match(/^```(\w*)/);
    if (fence) {
      const lang = fence[1] || 'text';
      const buf: string[] = [];
      i++;
      while (i < lines.length && !/^```/.test(lines[i])) {
        buf.push(lines[i]);
        i++;
      }
      i++; // consume the closing fence (absent while still streaming)
      blocks.push(<CodeBlock key={`b${key++}`} lang={lang} code={buf.join('\n')} />);
      continue;
    }

    if (/^###\s+/.test(line)) {
      blocks.push(
        <h3 key={`b${key++}`} className="mb-1.5 mt-3.5 text-[13.5px] font-semibold first:mt-0">
          {renderInline(line.replace(/^###\s+/, ''), `b${key}`)}
        </h3>,
      );
      i++;
      continue;
    }

    if (/^---\s*$/.test(line)) {
      blocks.push(<hr key={`b${key++}`} className="my-3 border-border" />);
      i++;
      continue;
    }

    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push(
        <ul key={`b${key++}`} className="mb-2.5 list-disc pl-[19px]">
          {items.map((item, n) => (
            <li key={n} className="mb-0.5 marker:text-muted-foreground">
              {renderInline(item, `b${key}-${n}`)}
            </li>
          ))}
        </ul>,
      );
      continue;
    }

    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push(
        <ol key={`b${key++}`} className="mb-2.5 list-decimal pl-[19px]">
          {items.map((item, n) => (
            <li key={n} className="mb-0.5 marker:text-muted-foreground">
              {renderInline(item, `b${key}-${n}`)}
            </li>
          ))}
        </ol>,
      );
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !/^(```|###\s|[-*]\s|\d+\.\s|---\s*$)/.test(lines[i])
    ) {
      para.push(lines[i]);
      i++;
    }
    blocks.push(
      <p key={`b${key++}`} className="mb-2.5 break-words last:mb-0">
        {renderInline(para.join(' '), `b${key}`)}
      </p>,
    );
  }

  return <div className={cn('text-[13.5px] leading-[1.62]', className)}>{blocks}</div>;
}
